import { type PostHog } from 'posthog-js';

/**
 * Aligns event capture with the impersonation state of the session.
 *
 * An impersonated session identifies as the impersonated user so every feature flag
 * (cohorts, email conditions, % rollouts, company-group conditions) resolves exactly as
 * that user sees it. Capture is switched off for the duration so admin activity never
 * lands on the customer's person: flag evaluation hits `/flags` regardless of the
 * opt-out, and the opt-out does not disable persistence with our config, so the
 * impersonated distinct_id survives page loads.
 *
 * posthog persists the opt-out in localStorage, so a session that ended while opted out
 * would stay silent forever — opting back in on every non-impersonated session is what
 * makes this self-correcting.
 */
export const syncImpersonationCapture = (posthog: PostHog, { isImpersonated }: { isImpersonated: boolean }): void => {
  const isOptedOut = posthog.has_opted_out_capturing();

  if (isImpersonated) {
    if (!isOptedOut) posthog.opt_out_capturing();
    return;
  }

  // captureEventName: false — opting back in captures a `$opt_in` event by default, which
  // would add a synthetic event to every session that follows an impersonation.
  if (isOptedOut) posthog.opt_in_capturing({ captureEventName: false });
};
